import { components } from "@/api-schema";
import { formatDateToLocale } from "@/common/helpers/format-date";
import { isRelocationOrderTerminalStatus } from "@/common/helpers/relocation-order-terminal-status";
import { cn } from "@/common/helpers/utils";
import { EmptyValue } from "@/components/ui/empty-value";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import i18next from "i18next";
import { JSX } from "react";

type RelocationOrderStatus = components["schemas"]["RelocationOrder"]["status"];

const SOON_OVERDUE_THRESHOLD_DAYS = 3;

function getDateAtMidnight(date: Date | string): Date {
  const dateObj = typeof date === "string" ? new Date(date) : new Date(date);
  dateObj.setHours(0, 0, 0, 0);
  return dateObj;
}

export function isPickupDateOverdue(pickupDate: string | null | undefined, status?: RelocationOrderStatus): boolean {
  if (!pickupDate || isRelocationOrderTerminalStatus(status)) {
    return false;
  }
  const pickup = getDateAtMidnight(pickupDate);
  const today = getDateAtMidnight(new Date());
  return pickup.getTime() < today.getTime();
}

export function isPickupDateOverdueSoon(
  pickupDate: string | null | undefined,
  status?: RelocationOrderStatus,
): boolean {
  if (!pickupDate || isRelocationOrderTerminalStatus(status)) {
    return false;
  }
  const pickup = getDateAtMidnight(pickupDate);
  const today = getDateAtMidnight(new Date());
  const soon = getDateAtMidnight(new Date(Date.now() + SOON_OVERDUE_THRESHOLD_DAYS * 24 * 60 * 60 * 1000));
  return pickup.getTime() <= soon.getTime() && pickup.getTime() >= today.getTime();
}

function renderDateWithTooltip(dateContent: JSX.Element, tooltipKey: string) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{dateContent}</TooltipTrigger>
        <TooltipContent>
          <p>{i18next.t(tooltipKey)}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function renderPickupDate(pickupDate: string | null | undefined, status?: RelocationOrderStatus) {
  const isOverdue = isPickupDateOverdue(pickupDate, status);
  const isOverdueSoon = isPickupDateOverdueSoon(pickupDate, status);

  const dateContent = (
    <span
      className={cn("truncate", isOverdue && "text-error font-semibold", isOverdueSoon && "text-warning font-semibold")}
    >
      {pickupDate ? formatDateToLocale(pickupDate, { dateOnly: true }) : <EmptyValue />}
    </span>
  );

  if (isOverdue) {
    return renderDateWithTooltip(dateContent, "fit:PickupDateOverdueTooltip");
  }

  if (isOverdueSoon) {
    return renderDateWithTooltip(dateContent, "fit:PickupDateSoonOverdueTooltip");
  }

  return dateContent;
}
